// /a/:id (your adaptation) and /s/:slug (shared link: public preview, full content for active accounts).
import { useEffect, useState } from "react";
import { getCatalogLesson, getPrinciple } from "../../shared/catalog";
import { describeParams, docFromAdaptation } from "../../shared/lesson-model";
import { api, ApiError } from "../lib/api";
import { Link } from "../lib/router";
import { useSession } from "../lib/session";
import type { Adaptation } from "../lib/types";
import { LessonDocView } from "../components/LessonDocView";
import { AdaptationActions } from "../components/AdaptationActions";
import { StageStrip } from "../components/StageStrip";
import { Paywall } from "../components/Paywall";

export default function AdaptationPage({ id, slug }: { id?: string; slug?: string }) {
  const { me } = useSession();
  const [a, setA] = useState<Adaptation | null>(null);
  const [preview, setPreview] = useState(false);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setA(null);
    setError("");
    setLocked(false);
    const p = slug ? api.shared(slug).then((r) => { setA(r.adaptation); setPreview(r.preview); }) : api.adaptation(id!).then((r) => { setA(r.adaptation); setPreview(false); });
    p.catch((e: ApiError) => (e.code === "NO_ACCESS" || e.code === "SIGN_IN_REQUIRED" ? setLocked(true) : setError(e.message)));
  }, [id, slug, me?.access.state]);

  if (locked) return <div className="page narrow-wide"><h1>Your saved adaptation</h1><Paywall what="your saved adaptations" /></div>;
  if (error) return <div className="page narrow empty"><h1>Can't open this lesson</h1><p>{error}</p><p><Link to="/">Browse the handbook's 50 lessons</Link>.</p></div>;
  if (!a) return <div className="page narrow"><p className="muted">Loading…</p></div>;
  const lesson = getCatalogLesson(a.lessonId)!;

  if (preview) {
    const o = a.output;
    return (
      <div className="page narrow-wide">
        <nav className="crumbs" aria-label="Breadcrumb"><span>Shared lesson</span></nav>
        <article className="doc">
          <p className="doc-kicker"><span className="code">{lesson.id}′</span> {lesson.topic}</p>
          <h1 className="doc-title">{o.title}</h1>
          <p className="doc-meta"><span>{o.level}</span><span>{o.duration} minutes</span><span>{o.skill}</span><span className="tag-adapted">Adapted</span></p>
          {o.summary && <p className="doc-summary">{o.summary}</p>}
          <StageStrip stages={o.stages} size="lg" showLabels />
          {o.ledger?.length > 0 && (
            <section className="doc-block ledger">
              <h3>What changed</h3>
              <ol>{o.ledger.map((l, i) => <li key={i}><p className="ledger-change">{l.change}</p><p className="ledger-cite">Handbook §{l.principle} · {getPrinciple(l.principle)?.title}</p></li>)}</ol>
            </section>
          )}
        </article>
        <Paywall what="the full adapted lesson, plus all 50 lessons" />
      </div>
    );
  }

  const doc = docFromAdaptation(lesson, a.output);
  const readOnly = !!slug;
  return (
    <div className="page narrow-wide">
      <nav className="crumbs" aria-label="Breadcrumb">
        {readOnly ? <span>Shared lesson</span> : <Link to="/saved">Saved</Link>} <span aria-hidden="true">/</span> <Link to={`/lesson/${lesson.id}`}>Original {lesson.id}</Link>
      </nav>
      <div className="result-head">
        <p className="muted small">Adapted for: {describeParams(a.params)}</p>
        <AdaptationActions a={a} onChange={setA} readOnly={readOnly} />
        <div className="actions-row">
          <Link className="btn-link" to={`/studio/${lesson.id}?level=${a.params.level}&duration=${a.params.duration}&classSize=${a.params.classSize}&tech=${a.params.tech}&mode=${a.params.mode}`}>Adapt it differently</Link>
        </div>
      </div>
      <LessonDocView doc={doc} />
      {readOnly && <p className="shared-foot">Adapted from lesson {lesson.id} of <i>The ESL Teacher's Handbook</i> by Melina Wong.</p>}
    </div>
  );
}
